import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useContext, useEffect, useRef, useState } from "preact/hooks";
import type {
	BrickedPictureOutput,
	Palette,
} from "@brickme/project-core/src/model/picture.ts";
import type { BuildSources } from "@brickme/project-core/src/image-build/create-bricked-image-from-source.ts";
import type { BuildWorker } from "./build-worker-bridge.ts";
import { useRenderMode } from "../picture-render/render-mode-context.tsx";
import { usePicture } from "../picture-edit/picture-edit-context.tsx";
import { ensureExhaustive } from "../utils/never.ts";

type BrickedBuildContextValue = {
	readonly brickedBuild: BrickedPictureOutput | undefined;
};

const brickedBuildContext = createContext<BrickedBuildContextValue | undefined>(
	undefined,
);

type PictureBuildProviderProps = {
	/** @deprecated Shouldn't need to use this. Will likely resolve with some refactoring insight */
	readonly onChange?: (brickedBuild: BrickedPictureOutput | undefined) => void;
	readonly buildWorker: BuildWorker;
	readonly palette: Palette;
	readonly buildSources: BuildSources;
	readonly children: ComponentChildren;
};

function PictureBuildProvider({
	buildWorker,
	buildSources,
	onChange,
	palette,
	children,
}: PictureBuildProviderProps) {
	const [brickedBuildValue, setBrickedBuild] =
		useState<BrickedBuildContextValue>({
			brickedBuild: undefined,
		});

	// Notify parent of changes
	const { brickedBuild } = brickedBuildValue;
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	useEffect(() => {
		onChangeRef.current?.(brickedBuild);
	}, [brickedBuild]);

	// Receive bricked build from worker
	useEffect(() => {
		function onBrickedBuild(brickedBuild: BrickedPictureOutput) {
			setBrickedBuild({ brickedBuild });
		}
		buildWorker.addEventListener("brickedBuild", onBrickedBuild);

		return () => {
			buildWorker.removeEventListener("brickedBuild", onBrickedBuild);
		};
	}, [buildWorker]);

	// Notify worker of palette
	useEffect(() => {
		buildWorker.setPalette(palette);
		return () => {
			buildWorker.setPalette(undefined);
		};
	}, [buildWorker, palette]);

	// Notify worker of build sources
	useEffect(() => {
		buildWorker.setBuildSources(buildSources);

		return () => {
			buildWorker.setBuildSources(undefined);
		};
	}, [buildWorker, buildSources]);

	// Transfer picture to worker when changes. Pause builds when source rendering mode
	const { activeRenderMode } = useRenderMode();
	const { picture } = usePicture();
	useEffect(() => {
		switch (activeRenderMode) {
			case "source":
				buildWorker.clearPicture();
				// Clear build. Without this we get a flash of "old" bricked when
				// we turn back on.
				setBrickedBuild({ brickedBuild: undefined });
				break;
			case "bricked":
			case "solid":
				buildWorker.setPicture(picture);
				break;
			default:
				ensureExhaustive(activeRenderMode);
		}
		return () => {
			buildWorker.clearPicture();
		};
	}, [activeRenderMode, buildWorker, picture]);

	return (
		<brickedBuildContext.Provider value={brickedBuildValue}>
			{children}
		</brickedBuildContext.Provider>
	);
}

function useCurrentBrickedBuild() {
	const context = useContext(brickedBuildContext);
	if (context === undefined) {
		throw new Error("useCurrentBrickedBuild must be used within context");
	}
	return context.brickedBuild;
}

export { PictureBuildProvider, useCurrentBrickedBuild };
